import React from 'react';
import { Controller } from 'react-hook-form';
import {
  TextField,
  Grid,
  Checkbox,
  Typography,
  Autocomplete,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import RateCardFormItemAutoComplete from '../RateCardFormItemAutoComplete';
import FilteredRateTable from './FilteredConsumableRateTable';
import { colors } from '@material-ui/core';
import BranchAutocomplete from '../Utils/BranchAutocomplete';
import { currencyType } from 'api/organizationApi';

const ConsumableRateFormFields = ({
  control,
  errors,
  // isEditMode,
  openBuyRateForm,
  watch,
  consumableItemNames,
  // editRateData,
  filteredBranches,
  openSellRateForm,
  clients,
  sellTableRows,
  buyTableRows,
  setValue,
}) => {
  const isDefaultRate = watch('isDefault');
  const clientId = watch('clientId');
  const branch = watch('branch');
  const consumableItemId = watch('consumableItemId');
  const effectiveFrom = watch('effectiveFrom');

  return (
    <Grid container spacing={2} sx={{ padding: '1rem' }}>
      <Grid item xs={12} md={6}>
        <Grid item xs={12} sx={{ marginBottom: '1rem', marginTop: '1.8rem' }}>
          <RateCardFormItemAutoComplete
            control={control}
            rules={{ required: 'Name is required' }}
            errors={errors}
            label="Name *"
            name={'consumableItemId'}
            items={consumableItemNames}
            // disabled={isEditMode}
          />
        </Grid>

        <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
          <Controller
            fullWidth
            key={'rates'}
            rules={{
              required: { value: true, message: 'Required' },
              min: {
                value: 0,
                message: 'Value is Required and cannot be negative',
              },
            }}
            as={
              <TextField
                fullWidth
                error={!!(openBuyRateForm ? errors.buyRate : errors.sellRate)}
                helperText={
                  openBuyRateForm
                    ? errors.buyRate?.message
                    : errors.sellRate?.message
                }
                label={openBuyRateForm ? 'Buy Rate *' : 'Sell Rate *'}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencyType}
                    </InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                  },
                }}
                value={openBuyRateForm ? 'buyRate' : 'sellRate'}
                // disabled={
                //    openSellRateForm
                //       ? false
                //       : openBuyRateForm
                //       ? false
                //       : true
                // }
              />
            }
            control={control}
            name={openBuyRateForm ? 'buyRate' : 'sellRate'}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            marginBottom: '1rem',
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            borderRadius: '5px',
            marginTop: '1rem',
          }}>
          <Controller
            name="isDefault"
            key={'isDefault'}
            control={control}
            render={(props) => (
              <FormControlLabel
                label={`  Use as Default ${
                  openBuyRateForm ? 'Buy' : 'Sell'
                } Rate`}
                control={
                  <Checkbox
                    checked={props.value ?? false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        props.onChange('branchId', null);
                        props.onChange('clientId', null);
                      }
                      props.onChange(Boolean(e.target.checked));
                    }}
                    name="removeHeader"
                    color="primary"
                  />
                }
              />
            )}
          />
        </Grid>
        {consumableItemId && (
          <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
            <FilteredRateTable
              sellTableRows={sellTableRows}
              buyTableRows={buyTableRows}
              openSellRateForm={openSellRateForm}
              openBuyRateForm={openBuyRateForm}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
          <Typography
            variant="body1"
            sx={{ mb: '0.3rem', pl: 0.5, color: colors.grey[600] }}>
            Effective From
          </Typography>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              control={control}
              name={'effectiveFrom'}
              error={errors.effectiveFrom}
              rules={{
                required: {
                  value: true,
                  message: 'Effective From date is required',
                },
              }}
              render={(props) => {
                return (
                  <DatePicker
                    autoOk
                    fullWidth
                    // className={classes.mTop}
                    orientation="landscape"
                    error={errors.effectiveFrom}
                    variant="static"
                    helperText={errors?.date?.message}
                    openTo="date"
                    value={effectiveFrom}
                    defaultValue={effectiveFrom ? effectiveFrom : new Date()}
                    onChange={(e) => setValue('effectiveFrom', e)}
                  />
                );
              }}
            />
          </MuiPickersUtilsProvider>
          {errors.effectiveFrom && (
            <Typography
              variant="caption"
              color="error"
              sx={{ paddingTop: 1, display: 'block' }}>
              {errors.effectiveFrom.message}
            </Typography>
          )}
        </Grid>

        {!isDefaultRate && (
          <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
            <BranchAutocomplete
              name="branch"
              label="Select Branch "
              control={control}
              rules={{
                required:
                  !isDefaultRate && !clientId
                    ? {
                        value: true,
                        message: openBuyRateForm
                          ? 'Branch selection is required'
                          : 'Branch selection is required if Client is not selected',
                      }
                    : false,
              }}
              errors={errors}
              branches={filteredBranches}
              branchId={branch?.id}
            />
          </Grid>
        )}

        {openSellRateForm && !isDefaultRate && (
          <Grid item xs={12} sx={{ marginBottom: '1rem' }}>
            <Controller
              name="clientId"
              key={'clientId'}
              rules={{
                required:
                  !isDefaultRate && !branch
                    ? {
                        value: true,
                        message:
                          'Client selection is required if Branch is not selected',
                      }
                    : false,
              }}
              control={control}
              render={(props) => (
                <Autocomplete
                  fullWidth
                  autoHighlight
                  autoSelect
                  openOnFocus
                  options={clients || []}
                  getOptionLabel={(option) => String(option?.legalName || '')}
                  noOptionsText="No matches"
                  value={
                    clients?.find((item) => item.id === props.value) || null
                  }
                  onChange={(_, data) => props.onChange(data?.id ?? null)}
                  // disabled={isEditMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.clientId}
                      helperText={errors.clientId?.message}
                      label="Select Client "
                      variant="outlined"
                      fullWidth
                      // disabled={isEditMode}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ConsumableRateFormFields;
